import React, { useState, useEffect } from 'react';
import useAppStore from '../../store/useAppStore.js';
import { downloadDatabaseBackup } from '../../services/adminApi.js';

const AdminBackup = () => {
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedDataType, setSelectedDataType] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { admin, loadBranches } = useAppStore((state) => ({
    admin: state.admin,
    loadBranches: state.admin.loadBranches
  }));

  const branches = admin?.branches || [];

  useEffect(() => {
    if (!branches.length) {
      loadBranches();
    }
  }, [loadBranches, branches.length]);

  const dataTypes = [
    { value: 'products', label: 'Products' },
    { value: 'inventory', label: 'Inventory' },
    { value: 'expenses', label: 'Expenses' },
    { value: 'orders', label: 'Orders' },
    { value: 'users', label: 'Users' },
    { value: 'branches', label: 'Branches' },
    { value: 'all', label: 'All Data' }
  ];

  const handleExportAll = async () => {
    setIsLoading(true);
    try {
      const blob = await downloadDatabaseBackup();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `ice_cream_shop_backup_${timestamp}.sql`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      alert('Database backup downloaded successfully!');
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImportAll = async () => {
    setIsLoading(true);
    try {
      // TODO: Implement import all data API call
      alert('Import All Data functionality will be implemented');
    } catch (error) {
      console.error('Import failed:', error);
      alert('Import failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportSpecific = async () => {
    if (!selectedBranch || !selectedDataType) {
      alert('Please select a branch and data type');
      return;
    }

    setIsLoading(true);
    try {
      // TODO: Implement export specific data API call
      alert(`Export ${selectedDataType} for branch ${selectedBranch} will be implemented`);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImportSpecific = async () => {
    if (!selectedBranch || !selectedDataType) {
      alert('Please select a branch and data type');
      return;
    }

    setIsLoading(true);
    try {
      // TODO: Implement import specific data API call
      alert(`Import ${selectedDataType} for branch ${selectedBranch} will be implemented`);
    } catch (error) {
      console.error('Import failed:', error);
      alert('Import failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Data Backup & Restore</h2>
        <p className="text-gray-600 mb-6">
          Create and download a complete SQL backup of your ice cream shop database. You can also backup specific data for selected branches.
        </p>

        {/* Full Data Backup Section */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Full Data Backup</h3>
          <div className="flex gap-4">
            <button
              onClick={handleExportAll}
              disabled={isLoading}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <span>📤</span>
              Download SQL Backup
            </button>
            <button
              onClick={handleImportAll}
              disabled={isLoading}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <span>📥</span>
              Import All Data
            </button>
          </div>
        </div>

        {/* Specific Data Backup Section */}
        <div className="border-t border-gray-200 pt-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Specific Data Backup</h3>
          <p className="text-gray-600 mb-4">
            Select a branch and data type to backup or restore specific data.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Branch
              </label>
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Choose a branch...</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name} - {branch.location}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Data Type
              </label>
              <select
                value={selectedDataType}
                onChange={(e) => setSelectedDataType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Choose data type...</option>
                {dataTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={handleExportSpecific}
              disabled={isLoading || !selectedBranch || !selectedDataType}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <span>📤</span>
              Export Selected Data
            </button>
            <button
              onClick={handleImportSpecific}
              disabled={isLoading || !selectedBranch || !selectedDataType}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <span>📥</span>
              Import Selected Data
            </button>
          </div>
        </div>

        {/* Information Section */}
        <div className="border-t border-gray-200 pt-6 mt-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <span className="text-yellow-600 text-lg mr-3">⚠️</span>
              <div>
                <h4 className="text-yellow-800 font-semibold mb-2">Important Notes</h4>
                <ul className="text-yellow-700 text-sm space-y-1">
                  <li>• Always backup your data before importing new data</li>
                  <li>• Importing data will overwrite existing data for the selected type</li>
                  <li>• Make sure your backup files are in the correct format</li>
                  <li>• Contact support if you encounter any issues</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBackup;